import { useTranslation } from 'react-i18next';
/**
 * Edit Assembly dialog — the BIM "Edit Assembly" screen for layered types
 * (walls, floors, roofs, ceilings). Reads/writes the symbol's `layers` bag
 * through the typeProperties draft in the parent via the onApply callback.
 */

import { useState } from 'react';
import {
    Box,
    Button,
    HStack,
    IconButton,
    Input,
    Select,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    VStack,
} from '@chakra-ui/react';
import { AppDialog, DialogButton } from '../common/AppDialog';
import { ChevronDown, ChevronUp, Copy, Trash2 } from 'lucide-react';
import { ASSEMBLY_FUNCTIONS, AssemblyLayerRow } from '../../config/typeParameterSchemas';
import { useMaterialNameOptions } from './materialCatalog';

interface EditAssemblyDialogProps {
    isOpen: boolean;
    onClose: () => void;
    layerStation: string;
    structure: string;
    layers: AssemblyLayerRow[];
    onApply: (layers: AssemblyLayerRow[]) => void;
}

const STRUCTURE_OPTIONS = ['Exterior', 'Interior', 'Core (Shaft)', 'Foundation'];
const STATION_OPTIONS = ['Finish 1 [Face Exterior]', 'Core Boundary [Exterior]', 'Core Center', 'Core Boundary [Interior]', 'Finish 2 [Face Interior]'];

export function EditAssemblyDialog({ isOpen, onClose, layerStation, structure, layers, onApply }: EditAssemblyDialogProps) {
    const { t } = useTranslation();
    const materialNames = useMaterialNameOptions();
    const [draftStation, setDraftStation] = useState(layerStation);
    const [draftStructure, setDraftStructure] = useState(structure);
    const [draftLayers, setDraftLayers] = useState<AssemblyLayerRow[]>(() => layers.map(l => ({ ...l })));

    const totalThickness = draftLayers.reduce((sum, l) =>sum + (l.thickness || 0), 0); const patchLayer = (index: number, patch: Partial<AssemblyLayerRow>) => {
        setDraftLayers(prev => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));
    };

    const moveLayer = (index: number, dir: -1 | 1) => {
        setDraftLayers(prev => {
            const next = [...prev];
            const target = index + dir;
            if (target < 0 || target >= next.length) return prev;
            [next[index], next[target]] = [next[target], next[index]];
            return next;
        });
    };

    const duplicateLayer = (index: number) => {
        setDraftLayers(prev => {
            const next = [...prev];
            next.splice(index + 1, 0, { ...prev[index] });
            return next;
        });
    };

    const deleteLayer = (index: number) => {
        setDraftLayers(prev => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
    };

    const inputBg = {
        bg: 'var(--color-bg-primary)',
        borderColor: 'var(--color-border)',
    } as const;

    return (
        <AppDialog
            isOpen={isOpen}
            onClose={onClose}
            size="xl"
            scrollBehavior="inside"
            title={t('misc.editAssembly')}
            footer={
                <>
                    <DialogButton variant="ghost" onClick={onClose}>{t('misc.cancel')}</DialogButton>
                    <DialogButton
                        variant="primary"
                        onClick={() => {
                            onApply(draftLayers);
                            onClose();
                        }}
                    >{t('misc.oK')}</DialogButton>
                </>
            }
        >
            <VStack spacing={3} align="stretch">
                <HStack spacing={4}>
                    <Text fontSize="xs" color="var(--color-text-secondary)" w="140px" flexShrink={0}>{t('misc.structure')}</Text>
                    <Select
                        size="sm"
                        w="240px"
                        value={draftStructure}
                        onChange={(e) => setDraftStructure(e.target.value)}
                        {...inputBg}
                    >
                        {STRUCTURE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </Select>
                </HStack>
                <HStack spacing={4}>
                    <Text fontSize="xs" color="var(--color-text-secondary)" w="140px" flexShrink={0}>{t('misc.layerStationReference')}</Text>
                    <Select
                        size="sm"
                        w="280px"
                        value={draftStation}
                        onChange={(e) => setDraftStation(e.target.value)}
                        {...inputBg}
                    >
                        {STATION_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </Select>
                </HStack>

                <Box overflowX="auto">
                    <Table size="sm" variant="simple">
                        <Thead>
                            <Tr>
                                <Th fontSize="xs" color="var(--color-text-secondary)">{t('misc.layer')}</Th>
                                <Th fontSize="xs" color="var(--color-text-secondary)" w="170px">{t('misc.function')}</Th>
                                <Th fontSize="xs" color="var(--color-text-secondary)" w="200px">{t('misc.material')}</Th>
                                <Th fontSize="xs" color="var(--color-text-secondary)" w="110px">{t('misc.thickness')}</Th>
                                <Th fontSize="xs" color="var(--color-text-secondary)" w="90px">{t('misc.wraps')}</Th>
                                <Th fontSize="xs" color="var(--color-text-secondary)" w="140px">{t('misc.actions')}</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {draftLayers.map((layer, index) => (
                                <Tr key={index}>
                                    <Td fontSize="xs">{index + 1}</Td>
                                    <Td>
                                        <Select
                                            size="xs"
                                            value={layer.function}
                                            onChange={(e) => patchLayer(index, { function: e.target.value })}
                                            {...inputBg}
                                        >
                                            {ASSEMBLY_FUNCTIONS.map(f => <option key={f} value={f}>{f}</option>)}
                                        </Select>
                                    </Td>
                                    <Td>
                                        <Select
                                            size="xs"
                                            value={layer.materialId}
                                            onChange={(e) => patchLayer(index, { materialId: e.target.value })}
                                            {...inputBg}
                                        >
                                            {materialNames.map(m => <option key={m} value={m}>{m}</option>)}
                                        </Select>
                                    </Td>
                                    <Td>
                                        <Input
                                            size="xs"
                                            type="number"
                                            step={1}
                                            value={Math.round(layer.thickness * 1000)}
                                            onChange={(e) => patchLayer(index, { thickness: (parseFloat(e.target.value) || 0) / 1000 })}
                                            {...inputBg}
                                        />
                                    </Td>
                                    <Td>
                                        <Select
                                            size="xs"
                                            value={layer.wraps ? 'Yes' : 'No'}
                                            onChange={(e) => patchLayer(index, { wraps: e.target.value === 'Yes' })}
                                            {...inputBg}
                                        >
                                            <option value="Yes">{t('misc.yes')}</option>
                                            <option value="No">{t('misc.no')}</option>
                                        </Select>
                                    </Td>
                                    <Td>
                                        <HStack spacing={0}>
                                            <IconButton aria-label={t('misc.moveUp')} icon={<ChevronUp size={12} />} size="xs" variant="ghost" isDisabled={index === 0} onClick={() => moveLayer(index, -1)} />
                                            <IconButton aria-label={t('misc.moveDown')} icon={<ChevronDown size={12} />} size="xs" variant="ghost" isDisabled={index === draftLayers.length - 1} onClick={() => moveLayer(index, 1)} />
                                            <IconButton aria-label={t('misc.duplicate')} icon={<Copy size={12} />} size="xs" variant="ghost" onClick={() => duplicateLayer(index)} />
                                            <IconButton aria-label={t('misc.delete')} icon={<Trash2 size={12} />} size="xs" variant="ghost" color="var(--color-error)" isDisabled={draftLayers.length <= 1} onClick={() => deleteLayer(index)} />
                                        </HStack>
                                    </Td>
                                </Tr>
                            ))}
                            <Tr>
                                <Td colSpan={3} fontSize="xs" color="var(--color-text-secondary)">{t('misc.total')}</Td>
                                <Td fontSize="xs" fontWeight="semibold">{Math.round(totalThickness * 1000)} mm</Td>
                                <Td colSpan={2} />
                            </Tr>
                        </Tbody>
                    </Table>
                </Box>

                <Button
                    size="xs"
                    w="max-content"
                    variant="outline"
                    onClick={() => setDraftLayers(prev => [...prev, { function: 'Structure', materialId: 'Generic', thickness: 0.1, wraps: false }])}
                >{t('misc.insertLayer')}</Button>
            </VStack>
        </AppDialog>
    );
}
